import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Handshake,
  Map,
  Scale,
} from "lucide-react";

const PRODUCTS = [
  {
    tab: "Documents",
    name: "JaaGa Documents",
    Icon: FileText,
    title: "80+ document types across 27 states",
    desc: "Sale deeds, tax receipts and land records, straight from government portals. Many are instant, the rest are fetched by our team. No lines, no agents.",
    cta: "Fetch documents",
    to: "/states",
    items: ["Sale Deed & Certified Copies", "Encumbrance Certificate (EC)", "RERA Project Registration", "Prohibited Land Check", "Mutation & Tax Records"],
  },
  {
    tab: "Services",
    name: "JaaGa Services",
    Icon: ClipboardCheck,
    title: "Paperwork, done for you",
    desc: "From ownership checks to final registration, our team handles the paperwork while you track every step.",
    cta: "Explore services",
    to: "/services",
    items: ["Title Verification", "PTIN & Khata Services", "Encumbrance Filing", "Mutation Support", "End-to-End Registration"],
  },
  {
    tab: "Survey Maps",
    name: "Survey Maps",
    Icon: Map,
    title: "Find it, map it, keep it",
    desc: "Search land by survey number, view it on a satellite map, measure boundaries and save it to your locker.",
    cta: "Explore survey maps",
    to: "/maps",
    items: ["Search by Survey No. / Village", "Parcel & Boundary Map", "Satellite & Standard Layers", "EMI & Cost Calculators", "My Locker: Save & Track"],
  },
  {
    tab: "Listings",
    name: "JaaGa Listings",
    Icon: Building2,
    title: "Buy, sell, rent and lease property",
    desc: "Every listing is document-verified, so you deal only with genuine owners.",
    cta: "Explore listings",
    to: "/properties/listings",
    items: ["Buy verified properties", "Sell / list your property", "Rent & lease homes and land", "Document-verified listings", "Connect directly with owners"],
  },
  {
    tab: "Legal Module",
    name: "JaaGa Legal Module",
    Icon: Scale,
    title: "Automated due diligence, bank-ready",
    desc: "From document fetch to signed report, fully automated. JaaGa fetches the EC and prohibitory check, flags risks, summarises every document and fills a bank-approved template, ready to sign.",
    cta: "Explore Legal Module",
    href: "https://legal.jaaga.ai",
    items: ["EC Auto-fetch", "Prohibitory Check", "AI Summary of all documents", "Risks highlighted", "Bank-approved template"],
  },
  {
    tab: "Partner",
    name: "JaaGa Partner",
    Icon: Handshake,
    title: "For lawyers, firms and SRO partners",
    desc: "Tools for lawyers and local partners to pull documents, spot risks and prepare bank-ready legal opinions.",
    cta: "Become a partner",
    href: "https://partner.jaaga.ai/",
    items: ["AI-Assisted Due Diligence", "Auto-Fetch EC & Prohibitory", "Bank-Ready Legal Opinions", "SRO On-Ground Fetch & Assist", "Case Submission & Tracking"],
  },
];

const AUTOPLAY_MS = 6000;

const HomeProducts = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = PRODUCTS.length;
  const go = (n) => setIdx(((n % total) + total) % total);

  useEffect(() => {
    if (paused || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused, total]);

  return (
    <section className="mh-sec" aria-labelledby="mh-five-h" aria-roledescription="carousel">
      <div
        className="mh-wrap"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <div className="mh-head">
          <p className="mh-eyebrow">Six products. One mission.</p>
          <h2 id="mh-five-h" className="mh-h2">Everything your property needs, in one app</h2>
          <p className="mh-lede">
            From a first-time buyer to a big bank, JaaGa has the right product for every property need.
          </p>
        </div>

        <div className="mh-ptabs-row">
          <button type="button" className="mh-arrow" aria-label="Previous product" onClick={() => go(idx - 1)}>
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          <div className="mh-ptabs" role="tablist">
            {PRODUCTS.map((p, i) => (
              <button key={p.tab} type="button" role="tab" aria-selected={i === idx} onClick={() => go(i)}>
                {p.tab}
              </button>
            ))}
          </div>
          <button type="button" className="mh-arrow" aria-label="Next product" onClick={() => go(idx + 1)}>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="mh-pcar">
          <div className="mh-pctrack" style={{ transform: `translateX(-${idx * 100}%)` }}>
            {PRODUCTS.map((p, i) => {
              const hidden = i !== idx;
              const ctaInner = (
                <>
                  {p.cta}
                  <span aria-hidden="true">→</span>
                </>
              );
              return (
                <div key={p.tab} className="mh-pslot" role="group" aria-label={`${i + 1} of ${total}`} aria-hidden={hidden}>
                  <article className="mh-pslide">
                    <div>
                      <div className="mh-pslide-top">
                        <span className="mh-pslide-ic">
                          <p.Icon size={28} aria-hidden="true" />
                        </span>
                        <span>
                          <small>{p.name}</small>
                          <em>{i + 1} / {total}</em>
                        </span>
                      </div>
                      <h3>{p.title}</h3>
                      <p>{p.desc}</p>
                      {p.href ? (
                        <a className="mh-solid" href={p.href} target="_blank" rel="noopener noreferrer" tabIndex={hidden ? -1 : 0}>
                          {ctaInner}
                        </a>
                      ) : (
                        <Link className="mh-solid" to={p.to} onClick={() => window.scrollTo(0, 0)} tabIndex={hidden ? -1 : 0}>
                          {ctaInner}
                        </Link>
                      )}
                    </div>
                    <div className="mh-incl">
                      <p>What’s included</p>
                      <ul>
                        {p.items.map((it) => (
                          <li key={it}>
                            <CheckCircle2 size={18} aria-hidden="true" />
                            {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeProducts;
